import { supabase } from '../lib/supabase';

export interface LocationQuantity {
  locationId: string;
  locationName: string;
  quantity: number;
}

export interface ProductLocationInventoryRecord {
  id: string;
  product_id: string;
  location_id: string;
  admin_id: string;
  quantity: number;
  location?: {
    id: string;
    name: string;
  };
}

export interface ShopifyLocationMapping {
  product_location_id: string;
  shopify_location_name: string;
}

const DEFAULT_LOCATIONS = ['Warehouse', 'Shop'];

export function isWarehouseLocationName(name: string): boolean {
  const lower = name.toLowerCase().trim();
  if (lower === 'warehouse') return true;
  return lower.includes('warehouse') || lower.includes('wearhouse');
}

export function resolveWarehouseLocationFromList(
  locations: { id: string; name: string }[],
  shopifyMappings: ShopifyLocationMapping[] = []
): { id: string; name: string } | null {
  if (locations.length === 0) return null;

  const mappedWarehouseLocations = locations.filter(loc =>
    shopifyMappings.some(m => m.product_location_id === loc.id)
    && isWarehouseLocationName(loc.name)
  );

  if (mappedWarehouseLocations.length === 1) {
    return mappedWarehouseLocations[0];
  }

  if (mappedWarehouseLocations.length > 1) {
    const exactMapped = mappedWarehouseLocations.find(
      l => l.name.toLowerCase() === 'warehouse'
    );
    if (exactMapped) return exactMapped;
    return mappedWarehouseLocations[0];
  }

  const exact = locations.find(l => l.name.toLowerCase() === 'warehouse');
  if (exact) return exact;

  return locations.find(l => isWarehouseLocationName(l.name)) || null;
}

export async function resolveWarehouseLocation(
  adminId: string
): Promise<{ id: string; name: string } | null> {
  const locations = await ensureDefaultLocations(adminId);
  const mappings = await fetchShopifyLocationMappings(adminId);
  return resolveWarehouseLocationFromList(locations, mappings);
}

async function ensureLegacyProductQuantityInWarehouse(
  productId: string,
  adminId: string,
  warehouseLocationId: string
): Promise<boolean> {
  const { data: anyPli } = await supabase
    .from('product_location_inventory')
    .select('id')
    .eq('product_id', productId)
    .eq('admin_id', adminId)
    .limit(1);

  if (anyPli && anyPli.length > 0) return true;

  const { data: product } = await supabase
    .from('products')
    .select('quantity')
    .eq('id', productId)
    .eq('admin_id', adminId)
    .maybeSingle();

  if (!product || (product.quantity ?? 0) <= 0) return true;

  const { error } = await supabase
    .from('product_location_inventory')
    .insert({
      product_id: productId,
      location_id: warehouseLocationId,
      admin_id: adminId,
      quantity: product.quantity
    });

  if (error) {
    console.error('Error seeding legacy product quantity to warehouse:', error);
    return false;
  }

  return true;
}

export async function ensureDefaultLocations(adminId: string): Promise<{ id: string; name: string }[]> {
  const { data: existingLocations } = await supabase
    .from('product_locations')
    .select('id, name')
    .eq('admin_id', adminId);

  const locations = existingLocations || [];
  const existingNames = locations.map(l => l.name.toLowerCase());

  for (const locationName of DEFAULT_LOCATIONS) {
    if (!existingNames.includes(locationName.toLowerCase())) {
      const { data: newLocation } = await supabase
        .from('product_locations')
        .insert({ admin_id: adminId, name: locationName })
        .select('id, name')
        .single();
      if (newLocation) {
        locations.push(newLocation);
      }
    }
  }

  return locations;
}

export async function fetchLocationsForAdmin(adminId: string): Promise<{ id: string; name: string }[]> {
  const { data } = await supabase
    .from('product_locations')
    .select('id, name')
    .eq('admin_id', adminId)
    .order('name');
  return data || [];
}

export async function fetchLocationQuantitiesForProduct(
  productId: string,
  adminId: string
): Promise<LocationQuantity[]> {
  const { data } = await supabase
    .from('product_location_inventory')
    .select(`
      id,
      location_id,
      quantity,
      location:product_locations(id, name)
    `)
    .eq('product_id', productId)
    .eq('admin_id', adminId);

  if (!data) return [];

  return data
    .filter(record => record.location)
    .map(record => ({
      locationId: record.location_id,
      locationName: (record.location as any).name,
      quantity: record.quantity
    }));
}

export async function fetchLocationQuantitiesForProducts(
  productIds: string[],
  adminId: string
): Promise<Map<string, LocationQuantity[]>> {
  if (productIds.length === 0) return new Map();

  const result = new Map<string, LocationQuantity[]>();
  const BATCH_SIZE = 100;

  const batches: string[][] = [];
  for (let i = 0; i < productIds.length; i += BATCH_SIZE) {
    batches.push(productIds.slice(i, i + BATCH_SIZE));
  }

  const fetchBatch = async (batchIds: string[], retryCount = 0): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('product_location_inventory')
        .select(`
          product_id,
          location_id,
          quantity,
          location:product_locations(id, name)
        `)
        .in('product_id', batchIds)
        .eq('admin_id', adminId);

      if (error) {
        if (retryCount < 3) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          return fetchBatch(batchIds, retryCount + 1);
        }
        console.error('Failed to fetch location quantities batch:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      if (retryCount < 3) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return fetchBatch(batchIds, retryCount + 1);
      }
      console.error('Error fetching location quantities batch:', err);
      return [];
    }
  };

  const batchResults = await Promise.all(batches.map(batch => fetchBatch(batch)));

  for (const batchData of batchResults) {
    for (const record of batchData) {
      if (!record.location) continue;

      const productId = record.product_id;
      if (!result.has(productId)) {
        result.set(productId, []);
      }

      result.get(productId)!.push({
        locationId: record.location_id,
        locationName: (record.location as any).name,
        quantity: record.quantity
      });
    }
  }

  return result;
}

export async function saveLocationQuantities(
  productId: string,
  adminId: string,
  quantities: { locationId: string; quantity: number }[]
): Promise<boolean> {
  try {
    const { data: existingRecords } = await supabase
      .from('product_location_inventory')
      .select('id, location_id')
      .eq('product_id', productId)
      .eq('admin_id', adminId);

    const existingByLocation = new Map(
      (existingRecords || []).map(r => [r.location_id, r.id])
    );

    const updates: any[] = [];
    const inserts: any[] = [];
    // Locations explicitly present in the form input (any quantity, including 0).
    // Rows for locations NOT in this set are the ones the caller intentionally
    // removed and will be deleted; rows for locations in the set with quantity 0
    // are preserved as 0 so the warehouse/location assignment persists across
    // sell-outs (B2B catalog needs this to keep showing the item as Out of stock).
    const locationIdsInForm = new Set<string>();

    for (const { locationId, quantity } of quantities) {
      locationIdsInForm.add(locationId);
      const existingId = existingByLocation.get(locationId);
      const safeQty = quantity > 0 ? quantity : 0;

      if (existingId) {
        updates.push({
          id: existingId,
          quantity: safeQty,
          updated_at: new Date().toISOString()
        });
      } else if (safeQty > 0) {
        // Don't create fresh zero-qty rows: that would assign a product to a
        // location it was never at. Zero is only meaningful as a "kept" state.
        inserts.push({
          product_id: productId,
          location_id: locationId,
          admin_id: adminId,
          quantity: safeQty
        });
      }
    }

    const idsToDelete = (existingRecords || [])
      .filter(r => !locationIdsInForm.has(r.location_id))
      .map(r => r.id);

    if (idsToDelete.length > 0) {
      await supabase
        .from('product_location_inventory')
        .delete()
        .in('id', idsToDelete);
    }

    for (const update of updates) {
      const { error } = await supabase
        .from('product_location_inventory')
        .update({ quantity: update.quantity, updated_at: update.updated_at })
        .eq('id', update.id);
      if (error) {
        console.error('Error updating location quantity:', error);
        return false;
      }
    }

    if (inserts.length > 0) {
      const { error } = await supabase
        .from('product_location_inventory')
        .insert(inserts);
      if (error) {
        console.error('Error inserting location quantities:', error);
        return false;
      }
    }

    await updateProductsQuantityFromLocations(productId, adminId);

    return true;
  } catch (error) {
    console.error('Error saving location quantities:', error);
    return false;
  }
}

export async function updateProductsQuantityFromLocations(productId: string, adminId: string): Promise<boolean> {
  const { data: locationRows } = await supabase
    .from('product_location_inventory')
    .select('quantity')
    .eq('product_id', productId)
    .eq('admin_id', adminId);

  const total = (locationRows || []).reduce((sum, r) => sum + (r.quantity || 0), 0);

  const { error } = await supabase
    .from('products')
    .update({ quantity: total, updated_at: new Date().toISOString() })
    .eq('id', productId)
    .eq('admin_id', adminId);

  if (error) {
    console.error('Error updating products.quantity from locations:', error);
    return false;
  }
  return true;
}

export async function addToWarehouseLocation(
  productId: string,
  adminId: string,
  quantityToAdd: number
): Promise<boolean> {
  if (quantityToAdd <= 0) return true;

  try {
    const warehouseLocation = await resolveWarehouseLocation(adminId);
    if (!warehouseLocation) {
      console.error('Could not find or create Warehouse location');
      return false;
    }

    const legacyOk = await ensureLegacyProductQuantityInWarehouse(
      productId,
      adminId,
      warehouseLocation.id
    );
    if (!legacyOk) return false;

    const { data: existing } = await supabase
      .from('product_location_inventory')
      .select('id, quantity')
      .eq('product_id', productId)
      .eq('location_id', warehouseLocation.id)
      .eq('admin_id', adminId)
      .maybeSingle();

    const newQuantity = (existing?.quantity ?? 0) + quantityToAdd;

    if (existing) {
      const { error } = await supabase
        .from('product_location_inventory')
        .update({ quantity: newQuantity, updated_at: new Date().toISOString() })
        .eq('id', existing.id);
      if (error) {
        console.error('Error adding to warehouse location:', error);
        return false;
      }
    } else {
      const { error } = await supabase
        .from('product_location_inventory')
        .insert({
          product_id: productId,
          location_id: warehouseLocation.id,
          admin_id: adminId,
          quantity: newQuantity
        });
      if (error) {
        console.error('Error inserting warehouse location inventory:', error);
        return false;
      }
    }

    return updateProductsQuantityFromLocations(productId, adminId);
  } catch (error) {
    console.error('Error in addToWarehouseLocation:', error);
    return false;
  }
}

export async function deductFromWarehouseLocation(
  productId: string,
  adminId: string,
  quantityToDeduct: number
): Promise<boolean> {
  if (quantityToDeduct <= 0) return true;

  try {
    const warehouseLocation = await resolveWarehouseLocation(adminId);
    if (!warehouseLocation) {
      console.error('Could not find or create Warehouse location');
      return false;
    }

    const { data: existing } = await supabase
      .from('product_location_inventory')
      .select('id, quantity')
      .eq('product_id', productId)
      .eq('location_id', warehouseLocation.id)
      .eq('admin_id', adminId)
      .maybeSingle();

    const currentQty = existing?.quantity ?? 0;

    // Fail closed: never oversell. A missing location row means zero available,
    // which is insufficient for any positive deduction. Clamping to zero here
    // silently oversells and corrupts inventory, so we reject instead.
    if (!existing || currentQty < quantityToDeduct) {
      console.error(
        `Insufficient warehouse stock for product ${productId}: have ${currentQty}, need ${quantityToDeduct}`
      );
      return false;
    }

    const newQuantity = currentQty - quantityToDeduct;
    const { error } = await supabase
      .from('product_location_inventory')
      .update({ quantity: newQuantity, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
    if (error) {
      console.error('Error deducting from warehouse location:', error);
      return false;
    }

    return updateProductsQuantityFromLocations(productId, adminId);
  } catch (error) {
    console.error('Error in deductFromWarehouseLocation:', error);
    return false;
  }
}

export async function deductFromLocation(
  productId: string,
  adminId: string,
  locationId: string,
  quantityToDeduct: number
): Promise<boolean> {
  if (quantityToDeduct <= 0) return true;

  try {
    const { data: existing } = await supabase
      .from('product_location_inventory')
      .select('id, quantity')
      .eq('product_id', productId)
      .eq('location_id', locationId)
      .eq('admin_id', adminId)
      .maybeSingle();

    const currentQty = existing?.quantity ?? 0;

    // Fail closed: never oversell. A missing location row means zero available,
    // which is insufficient for any positive deduction. Clamping to zero here
    // silently oversells and corrupts inventory, so we reject instead.
    if (!existing || currentQty < quantityToDeduct) {
      console.error(
        `Insufficient stock at location ${locationId} for product ${productId}: have ${currentQty}, need ${quantityToDeduct}`
      );
      return false;
    }

    const newQuantity = currentQty - quantityToDeduct;
    const { error } = await supabase
      .from('product_location_inventory')
      .update({ quantity: newQuantity, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
    if (error) {
      console.error('Error deducting from location:', error);
      return false;
    }

    return updateProductsQuantityFromLocations(productId, adminId);
  } catch (error) {
    console.error('Error in deductFromLocation:', error);
    return false;
  }
}

export async function migrateProductToWarehouse(
  productId: string,
  adminId: string,
  quantity: number
): Promise<boolean> {
  if (quantity <= 0) return true;

  try {
    const { data: existingInventory } = await supabase
      .from('product_location_inventory')
      .select('id')
      .eq('product_id', productId)
      .eq('admin_id', adminId)
      .limit(1);

    if (existingInventory && existingInventory.length > 0) {
      return true;
    }

    const warehouseLocation = await resolveWarehouseLocation(adminId);

    if (!warehouseLocation) {
      console.error('Could not find or create Warehouse location');
      return false;
    }

    const { error } = await supabase
      .from('product_location_inventory')
      .insert({
        product_id: productId,
        location_id: warehouseLocation.id,
        admin_id: adminId,
        quantity
      });

    if (error) {
      console.error('Error migrating product to warehouse:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error migrating product to warehouse:', error);
    return false;
  }
}

export async function migrateAllProductsToWarehouse(adminId: string): Promise<number> {
  try {
    const warehouseLocation = await resolveWarehouseLocation(adminId);

    if (!warehouseLocation) return 0;

    const { data: products } = await supabase
      .from('products')
      .select('id, quantity')
      .eq('admin_id', adminId)
      .gt('quantity', 0);

    if (!products || products.length === 0) return 0;

    const { data: existingAtWarehouse } = await supabase
      .from('product_location_inventory')
      .select('product_id')
      .eq('admin_id', adminId)
      .eq('location_id', warehouseLocation.id);

    const productsAtWarehouse = new Set(
      (existingAtWarehouse || []).map(r => r.product_id)
    );

    const productsToMigrate = products.filter(
      p => !productsAtWarehouse.has(p.id)
    );

    if (productsToMigrate.length === 0) return 0;

    const upsertRecords = productsToMigrate.map(p => ({
      product_id: p.id,
      location_id: warehouseLocation.id,
      admin_id: adminId,
      quantity: p.quantity
    }));

    const batchSize = 100;
    let migratedCount = 0;

    for (let i = 0; i < upsertRecords.length; i += batchSize) {
      const batch = upsertRecords.slice(i, i + batchSize);
      const { error } = await supabase
        .from('product_location_inventory')
        .upsert(batch, {
          onConflict: 'product_id,location_id',
          ignoreDuplicates: true
        });

      if (!error) {
        migratedCount += batch.length;
      }
    }

    return migratedCount;
  } catch (error) {
    console.error('Error migrating products to warehouse:', error);
    return 0;
  }
}

export function calculateTotalQuantity(quantities: LocationQuantity[]): number {
  return quantities.reduce((sum, q) => sum + q.quantity, 0);
}

export interface ProductWithLocationQuantities {
  id: string;
  name: string;
  reference_code?: string;
  color_id?: string;
  size_id?: string;
  category?: string;
  image_url?: string;
  buy_price: number;
  sell_price: number;
  totalQuantity: number;
  locationQuantities: Record<string, number>;
  // Location IDs where this product has an actual product_location_inventory
  // record (i.e. it is assigned/stocked there, even at qty 0). Distinct from
  // locationQuantities, which is zero-filled for every location.
  locationIdsWithRecord: string[];
}

export interface LocationComparisonData {
  products: ProductWithLocationQuantities[];
  locations: { id: string; name: string }[];
  warehouseLocation: { id: string; name: string } | null;
}

export async function fetchInventoryWithLocationBreakdown(
  adminId: string
): Promise<LocationComparisonData> {
  const locations = await fetchLocationsForAdmin(adminId);
  const shopifyMappings = await fetchShopifyLocationMappings(adminId);
  const warehouseLocation = resolveWarehouseLocationFromList(locations, shopifyMappings);

  const { count, error: countError } = await supabase
    .from('products')
    .select('*', { count: 'exact', head: true })
    .eq('admin_id', adminId);

  if (countError) {
    console.error('Error fetching product count:', countError);
    return { products: [], locations, warehouseLocation };
  }

  const total = count || 0;
  if (total === 0) {
    return { products: [], locations, warehouseLocation };
  }

  const chunkSize = 1000;
  const products: Array<{
    id: string;
    name: string;
    reference_code?: string;
    color_id?: string;
    size_id?: string;
    category?: string;
    image_url?: string;
    purchase_price?: number;
    cost_price?: number;
    sell_price?: number;
    unit_price?: number;
    quantity?: number;
  }> = [];

  for (let offset = 0; offset < total; offset += chunkSize) {
    const { data, error: productsError } = await supabase
      .from('products')
      .select('id, name, reference_code, color_id, size_id, category, image_url, purchase_price, cost_price, sell_price, unit_price, quantity')
      .eq('admin_id', adminId)
      .order('name', { ascending: true })
      .range(offset, offset + chunkSize - 1);

    if (productsError) {
      console.error('Error fetching products chunk:', productsError);
      break;
    }
    if (data) products.push(...data);
  }

  const productIds = products.map(p => p.id);
  const locationQuantitiesMap = await fetchLocationQuantitiesForProducts(productIds, adminId);

  const productsWithLocations: ProductWithLocationQuantities[] = products.map(product => {
    const quantities = locationQuantitiesMap.get(product.id) || [];
    const locationQuantities: Record<string, number> = {};
    // Only the locations that actually have a PLI record for this product.
    const locationIdsWithRecord = quantities.map(q => q.locationId);

    for (const loc of locations) {
      const qty = quantities.find(q => q.locationId === loc.id);
      locationQuantities[loc.id] = qty?.quantity || 0;
    }

    return {
      id: product.id,
      name: product.name,
      reference_code: product.reference_code,
      color_id: product.color_id,
      size_id: product.size_id,
      category: product.category,
      image_url: product.image_url,
      buy_price: product.purchase_price ?? product.cost_price ?? 0,
      sell_price: product.sell_price ?? product.unit_price ?? 0,
      totalQuantity: product.quantity || 0,
      locationQuantities,
      locationIdsWithRecord
    };
  });

  return { products: productsWithLocations, locations, warehouseLocation };
}

export async function fetchShopifyLocationMappings(
  adminId: string
): Promise<ShopifyLocationMapping[]> {
  const { data, error } = await supabase
    .from('shopify_location_mappings')
    .select('product_location_id, shopify_location_name')
    .eq('admin_id', adminId)
    .eq('is_active', true)
    .not('product_location_id', 'is', null);

  if (error || !data) return [];

  return data
    .filter(d => d.product_location_id && d.shopify_location_name)
    .map(d => ({
      product_location_id: d.product_location_id as string,
      shopify_location_name: d.shopify_location_name as string,
    }));
}

export interface ComparisonStats {
  totalProducts: number;
  productsWithDifference: number;
  warehouseOnlyCount: number;
  otherLocationOnlyCount: number;
  warehouseTotalQty: number;
  otherLocationTotalQty: number;
  netDifference: number;
}

export function calculateComparisonStats(
  products: ProductWithLocationQuantities[],
  warehouseId: string,
  otherLocationId: string
): ComparisonStats {
  let productsWithDifference = 0;
  let warehouseOnlyCount = 0;
  let otherLocationOnlyCount = 0;
  let warehouseTotalQty = 0;
  let otherLocationTotalQty = 0;

  for (const product of products) {
    const warehouseQty = product.locationQuantities[warehouseId] || 0;
    const otherQty = product.locationQuantities[otherLocationId] || 0;

    warehouseTotalQty += warehouseQty;
    otherLocationTotalQty += otherQty;

    if (warehouseQty !== otherQty) {
      productsWithDifference++;
    }

    if (warehouseQty > 0 && otherQty === 0) {
      warehouseOnlyCount++;
    } else if (warehouseQty === 0 && otherQty > 0) {
      otherLocationOnlyCount++;
    }
  }

  return {
    totalProducts: products.length,
    productsWithDifference,
    warehouseOnlyCount,
    otherLocationOnlyCount,
    warehouseTotalQty,
    otherLocationTotalQty,
    netDifference: otherLocationTotalQty - warehouseTotalQty
  };
}
